
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, AlertCircle, Activity, Bug } from "lucide-react";
import { toast } from "sonner";
import { onCLS, onLCP, onINP, onFCP, onTTFB, type Metric } from "web-vitals";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPublicSiteRoutes } from "@/lib/site-routes";
import { runSiteAuditNow } from "@/lib/site-audit.functions";
import { runPainelHealthCheck, type HealthCheckResult } from "@/lib/admin/health.functions";
import {
  GTM_CONTAINER_ID,
  GA4_MEASUREMENT_ID,
  META_PIXEL_ID,
  META_CAPI_PIXEL_ID,
} from "@/lib/tracking-config";
import { getMyAccess } from "@/lib/admin/access.functions";
import { AdminGate } from "@/components/admin/AdminGate";
import { ManagedLinksCard } from "@/components/admin/ManagedLinksCard";




export const Route = createFileRoute("/admin/painel")({
  head: () => ({
    meta: [
      { title: "Admin — Painel técnico" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/painel"><PainelPage /></AdminGate>,
});

// Rotas vêm do routeTree gerado automaticamente — qualquer página nova em
// src/routes/ aparece aqui sem precisar editar o admin.
const SITE_ROUTES = getPublicSiteRoutes();
const ROUTES = SITE_ROUTES.map((r) => r.path);

type RouteStatus = { path: string; url?: string; status: "ok" | "warn" | "down" | "checking"; httpCode?: number; ms?: number; error?: string };
type ExternalLinkStatus = { url: string; status: "ok" | "warn" | "down"; httpCode?: number; ms: number; method: string; sourcePaths: string[]; error?: string };
type HeaderLinkStatus = { label: string; expectedUrl: string; path: string; found: boolean; ok: boolean; foundUrls: string[] };
type IntegrationStatus = { key: string; label: string; status: "ok" | "warn"; detail: string };

function PainelPage() {
  const navigate = useNavigate();
  const runAudit = useServerFn(runSiteAuditNow);
  const [authReady, setAuthReady] = useState(false);
  const [routes, setRoutes] = useState<RouteStatus[]>(
    ROUTES.map((p) => ({ path: p, status: "checking" })),
  );
  const [hasChecked, setHasChecked] = useState(false);
  const [externalLinks, setExternalLinks] = useState<ExternalLinkStatus[]>([]);
  const [headerLinks, setHeaderLinks] = useState<HeaderLinkStatus[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [auditMeta, setAuditMeta] = useState<{ generatedAt: string; baseUrl: string; routeCount: number; externalLinkCount: number } | null>(null);
  const [tags, setTags] = useState({
    metaPixel: false,
    gtm: false,
    ga4: false,
    dataLayerEvents: 0,
    dataLayerBreakdown: [] as { name: string; count: number }[],
    dataLayerRecent: [] as { name: string; ts?: string; preview: string }[],
    trackingCookies: [] as { name: string; present: boolean; hint: string }[],
    consent: null as null | Record<string, string>,
    scripts: [] as { src: string; async: boolean; defer: boolean }[],
  });
  const [codes, setCodes] = useState({
    gtm: GTM_CONTAINER_ID,
    ga4: GA4_MEASUREMENT_ID,
    pixel: META_PIXEL_ID,
    capi: META_CAPI_PIXEL_ID,
  });
  const [diag, setDiag] = useState<null | {
    ttfb: number;
    domContentLoaded: number;
    loadEvent: number;
    transferSize: number;
    userAgent: string;
    viewport: string;
    dpr: number;
    lang: string;
    tz: string;
    online: boolean;
    connection: string;
    mode: string;
    url: string;
    referrer: string;
    localStorageKeys: number;
    sessionStorageKeys: number;
    cookiesCount: number;
    serviceWorkers: number;
    doNotTrack: string;
  }>(null);

  // Web Vitals — mede APENAS o admin painel (nunca as LPs).
  const [vitals, setVitals] = useState<Record<string, { value: number; rating: string }>>({});

  // Erros do navegador capturados nesta sessão do admin.
  const [consoleErrors, setConsoleErrors] = useState<{ ts: string; type: "error" | "rejection"; message: string; source?: string }[]>([]);
  const errorsRef = useRef<{ ts: string; type: "error" | "rejection"; message: string; source?: string }[]>([]);

  // Sessão Supabase (leitura local, sem chamada de rede)
  const [sessionInfo, setSessionInfo] = useState<null | {
    email: string;
    userId: string;
    expiresAt: string;
    expiresInMin: number;
    provider: string;
    isAdmin: boolean | "verificando";
  }>(null);

  // Health check manual (fila CRM + integrações externas)
  const runHealth = useServerFn(runPainelHealthCheck);
  const [health, setHealth] = useState<null | { generatedAt: string; results: HealthCheckResult[] }>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Build info (client-side)
  const buildInfo = {
    mode: import.meta.env.MODE,
    commitSha: (import.meta.env.VITE_COMMIT_SHA as string | undefined) ?? "n/d",
    buildTime: (import.meta.env.VITE_BUILD_TIME as string | undefined) ?? "n/d",
    baseUrl: (import.meta.env.BASE_URL as string | undefined) ?? "/",
  };




  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) {
        navigate({ to: "/admin" });
        return;
      }
      setAuthReady(true);
      // Info da sessão (local)
      const s = data.session;
      const expMs = (s.expires_at ?? 0) * 1000;
      const provisional = {
        email: s.user.email ?? "(sem email)",
        userId: s.user.id,
        expiresAt: new Date(expMs).toLocaleString("pt-BR"),
        expiresInMin: Math.max(0, Math.round((expMs - Date.now()) / 60000)),
        provider: (s.user.app_metadata?.provider as string) ?? "email",
        isAdmin: "verificando" as const,
      };
      setSessionInfo(provisional);
      // Acesso do painel (admin_members): master ou membro ativo
      try {
        const access = await getMyAccess();
        setSessionInfo({ ...provisional, isAdmin: access.isMaster || access.status === "active" });
      } catch {
        setSessionInfo({ ...provisional, isAdmin: false });
      }
    });
  }, [navigate]);

  // Erros globais capturados APENAS enquanto o painel está montado.
  useEffect(() => {
    if (!authReady) return;
    const push = (entry: { ts: string; type: "error" | "rejection"; message: string; source?: string }) => {
      errorsRef.current = [entry, ...errorsRef.current].slice(0, 20);
      setConsoleErrors([...errorsRef.current]);
    };
    const onErr = (e: ErrorEvent) => push({
      ts: new Date().toLocaleTimeString("pt-BR"),
      type: "error",
      message: e.message || String(e.error),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
    });
    const onRej = (e: PromiseRejectionEvent) => push({
      ts: new Date().toLocaleTimeString("pt-BR"),
      type: "rejection",
      message: e.reason instanceof Error ? e.reason.message : String(e.reason),
    });
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, [authReady]);

  // Web Vitals — apenas este documento (o admin painel).
  useEffect(() => {
    if (!authReady) return;
    const record = (m: Metric) => {
      setVitals((prev) => ({ ...prev, [m.name]: { value: Math.round(m.value * 100) / 100, rating: m.rating } }));
    };
    onCLS(record); onLCP(record); onINP(record); onFCP(record); onTTFB(record);
  }, [authReady]);

  const doHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const r = await runHealth();
      setHealth(r);
      const down = r.results.filter((x) => x.status === "down").length;
      const warn = r.results.filter((x) => x.status === "warn").length;
      if (down > 0) toast.error(`${down} verificação(ões) com falha`);
      else if (warn > 0) toast.warning(`${warn} verificação(ões) com atenção`);
      else toast.success("Todas as verificações OK");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao rodar health check");
    } finally {
      setHealthLoading(false);
    }
  };



  const checkRoutes = async () => {
    setHasChecked(true);
    setRoutes(ROUTES.map((p) => ({ path: p, status: "checking" })));
    setExternalLinks([]);
    setHeaderLinks([]);
    setIntegrations([]);
    setAuditMeta(null);
    try {
      const audit = await runAudit();
      setRoutes(audit.routes as RouteStatus[]);
      setExternalLinks(audit.externalLinks as ExternalLinkStatus[]);
      setHeaderLinks(audit.headerLinks as HeaderLinkStatus[]);
      setIntegrations(audit.integrations as IntegrationStatus[]);
      setAuditMeta({
        generatedAt: audit.generatedAt,
        baseUrl: audit.baseUrl,
        routeCount: audit.evidence.routeCount,
        externalLinkCount: audit.evidence.externalLinkCount,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao executar auditoria real");
      setRoutes(ROUTES.map((path) => ({ path, status: "down", error: "Auditoria falhou" })));
    }
  };

  const checkTags = () => {
    const w = window as unknown as {
      fbq?: unknown;
      dataLayer?: unknown[];
      google_tag_manager?: Record<string, unknown>;
      gtag?: unknown;
    };
    const scripts = Array.from(document.scripts)
      .filter((s) => s.src)
      .map((s) => ({ src: s.src, async: s.async, defer: s.defer }));

    // dataLayer breakdown
    const dl = Array.isArray(w.dataLayer) ? w.dataLayer : [];
    const counts = new Map<string, number>();
    const recent: { name: string; ts?: string; preview: string }[] = [];
    let consent: Record<string, string> | null = null;
    for (const raw of dl) {
      const item = raw as Record<string, unknown> | unknown[];
      let name = "(anônimo)";
      if (Array.isArray(item)) {
        if (item[0] === "consent") {
          name = `consent:${String(item[1] ?? "")}`;
          const payload = item[2] as Record<string, string> | undefined;
          if (payload && typeof payload === "object") consent = { ...(consent || {}), ...payload };
        } else if (typeof item[0] === "string") {
          name = `gtag:${item[0]}`;
        }
      } else if (item && typeof item === "object") {
        const ev = (item as Record<string, unknown>).event;
        if (typeof ev === "string") name = ev;
      }
      counts.set(name, (counts.get(name) || 0) + 1);
      const preview = (() => {
        try { return JSON.stringify(item).slice(0, 140); } catch { return String(item); }
      })();
      recent.push({ name, preview });
    }
    const breakdown = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Tracking cookies (name-only, no value)
    const cookieMap = new Map<string, string>();
    document.cookie.split(";").forEach((c) => {
      const [k, ...rest] = c.trim().split("=");
      if (k) cookieMap.set(k, rest.join("="));
    });
    const trackingCookies = [
      { name: "_fbp", hint: "Meta Pixel browser id" },
      { name: "_fbc", hint: "Meta click id (fbclid)" },
      { name: "_ga", hint: "GA4 client id" },
      { name: "_gcl_au", hint: "Google Ads conversion linker" },
      { name: "_gid", hint: "GA session id" },
      { name: "rdtrk", hint: "RD Station tracker" },
    ].map((c) => ({ ...c, present: cookieMap.has(c.name) }));

    setTags({
      metaPixel: typeof w.fbq === "function",
      gtm: !!w.google_tag_manager || (dl.length > 0 && scripts.some((s) => s.src.includes("gtm.js"))),
      ga4: typeof w.gtag === "function" || scripts.some((s) => s.src.includes("gtag/js")),
      dataLayerEvents: dl.length,
      dataLayerBreakdown: breakdown,
      dataLayerRecent: recent.slice(-20).reverse(),
      trackingCookies,
      consent,
      scripts,
    });

    // Try to extract the exact tracking IDs from the live scripts.
    const allScriptText = Array.from(document.scripts)
      .map((s) => `${s.src || ""} ${s.textContent || ""}`)
      .join(" ");

    const gtmMatch = allScriptText.match(/gtm\.js\?id=(GTM-[A-Z0-9]+)/);
    const gaMatch = allScriptText.match(/gtag\/js\?id=(G-[A-Z0-9]+)/);

    const pixelIds = new Set<string>();
    const pixelRegex = /fbq\('init',\s*['"](\d+)['"]\)/g;
    let m: RegExpExecArray | null;
    while ((m = pixelRegex.exec(allScriptText)) !== null) {
      pixelIds.add(m[1]);
    }

    setCodes({
      gtm: gtmMatch?.[1] ?? GTM_CONTAINER_ID,
      ga4: gaMatch?.[1] ?? GA4_MEASUREMENT_ID,
      pixel: pixelIds.size > 0 ? Array.from(pixelIds).join(", ") : META_PIXEL_ID,
      capi: META_CAPI_PIXEL_ID,
    });

    // Browser diagnostics
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    const conn = (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection;
    const swPromise = navigator.serviceWorker?.getRegistrations?.() ?? Promise.resolve([]);
    swPromise.then((regs) => {
      setDiag({
        ttfb: nav ? Math.round(nav.responseStart - nav.requestStart) : 0,
        domContentLoaded: nav ? Math.round(nav.domContentLoadedEventEnd - nav.startTime) : 0,
        loadEvent: nav ? Math.round(nav.loadEventEnd - nav.startTime) : 0,
        transferSize: nav?.transferSize ?? 0,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}×${window.innerHeight}`,
        dpr: window.devicePixelRatio,
        lang: navigator.language,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        connection: conn ? `${conn.effectiveType ?? "?"} · ${conn.downlink ?? "?"} Mbps · RTT ${conn.rtt ?? "?"}ms` : "n/d",
        mode: import.meta.env.MODE,
        url: window.location.href,
        referrer: document.referrer || "(direto)",
        localStorageKeys: (() => { try { return localStorage.length; } catch { return 0; } })(),
        sessionStorageKeys: (() => { try { return sessionStorage.length; } catch { return 0; } })(),
        cookiesCount: cookieMap.size,
        serviceWorkers: regs.length,
        doNotTrack: navigator.doNotTrack ?? "unset",
      });
    });
  };


  useEffect(() => {
    if (!authReady) return;
    // Checagem de rotas é manual (botão "Verificar") para não gerar acessos falsos nas LPs.
    checkTags();
  }, [authReady]);

  if (!authReady) return null;

  const okCount = routes.filter((r) => r.status === "ok").length;
  const warnCount = routes.filter((r) => r.status === "warn").length;
  const downCount = routes.filter((r) => r.status === "down").length;
  const checking = routes.some((r) => r.status === "checking");
  const slowCount = routes.filter((r) => r.ms !== undefined && r.ms > 2000).length;
  const externalDownCount = externalLinks.filter((l) => l.status === "down").length;
  const externalWarnCount = externalLinks.filter((l) => l.status === "warn").length;
  const headerFailCount = headerLinks.filter((h) => !h.ok).length;
  const integrationWarnCount = integrations.filter((i) => i.status === "warn").length;
  const noTracking = !tags.metaPixel && !tags.gtm && !tags.ga4;

  let overall: "ok" | "warn" | "down" | "checking" = "ok";
  let overallLabel = "Sem falhas na auditoria";
  const reasons: string[] = [];
  if (!hasChecked) {
    overall = "checking";
    overallLabel = "Aguardando verificação";
    reasons.push("Clique em Verificar para checar rotas, links externos e cabeçalho.");
  } else if (checking) {
    overall = "checking";
    overallLabel = "Verificando...";
  } else if (downCount > 0 || externalDownCount > 0 || headerFailCount > 0) {
    overall = "down";
    overallLabel = "ALERTA!";
    if (downCount > 0) reasons.push(`${downCount} rota(s) quebrada(s)`);
    if (externalDownCount > 0) reasons.push(`${externalDownCount} link(s) externo(s) quebrado(s)`);
    if (headerFailCount > 0) reasons.push(`${headerFailCount} link(s) de cabeçalho incorreto(s)`);
  } else if (warnCount > 0 || externalWarnCount > 0 || integrationWarnCount > 0 || slowCount > 0 || noTracking) {
    overall = "warn";
    overallLabel = "Atenção";
    if (warnCount > 0) reasons.push(`${warnCount} aviso(s) (3xx/4xx)`);
    if (externalWarnCount > 0) reasons.push(`${externalWarnCount} link(s) externo(s) com aviso`);
    if (integrationWarnCount > 0) reasons.push(`${integrationWarnCount} integração(ões) sem secret`);
    if (slowCount > 0) reasons.push(`${slowCount} página(s) lenta(s) (>2s)`);
    if (noTracking) reasons.push("nenhum pixel/tag detectado no navegador atual");
  } else {
    reasons.push(`${okCount} rotas, ${externalLinks.length} links externos e ${headerLinks.length} links de cabeçalho verificados com evidência real`);
  }

  const ledColor =
    overall === "ok" ? "bg-emerald-500 shadow-[0_0_24px_rgba(16,185,129,0.7)]"
    : overall === "warn" ? "bg-amber-500 shadow-[0_0_24px_rgba(245,158,11,0.7)]"
    : overall === "down" ? "bg-destructive shadow-[0_0_24px_hsl(var(--destructive)/0.7)]"
    : "bg-muted-foreground/40";
  const ledTextColor =
    overall === "ok" ? "text-emerald-500"
    : overall === "warn" ? "text-amber-500"
    : overall === "down" ? "text-destructive"
    : "text-muted-foreground";

  return (
    <div className="min-h-screen px-3 py-6 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 mb-6 sm:flex sm:items-center sm:justify-between sm:mb-8">
          <div className="min-w-0">
            <Button asChild variant="outline" size="sm" className="mb-3">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao menu</Link>
            </Button>
            <h1 className="truncate text-2xl sm:text-3xl font-bold text-foreground">Painel técnico</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">Status geral do site, integrações e scripts.</p>
          </div>
          <Button variant="default" size="sm" onClick={() => { checkRoutes(); checkTags(); }} className="shrink-0">
            <RefreshCw className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">{hasChecked ? "Re-verificar" : "Verificar"}</span>
          </Button>
        </div>

        <Card className="mb-6 sm:mb-8 p-4 sm:p-6">
          <div className="flex items-start gap-4 sm:gap-5 sm:items-center flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className={`inline-block h-5 w-5 sm:h-6 sm:w-6 shrink-0 rounded-full ${ledColor} ${overall !== "checking" ? "animate-pulse" : ""}`}
                aria-label={`Status: ${overallLabel}`}
              />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground">Status geral</p>
                <p className={`text-xl sm:text-2xl font-bold ${ledTextColor}`}>{overallLabel}</p>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground flex-1 min-w-[200px]">
              <div className="break-words">{reasons.join(" • ")}</div>
              {auditMeta && (
                <div className="mt-1 text-[10px] sm:text-xs break-words">
                  Auditoria real: <span className="font-mono">{auditMeta.baseUrl}</span>
                  {" • "}{new Date(auditMeta.generatedAt).toLocaleString("pt-BR")}
                  {" • "}{auditMeta.routeCount} rotas
                  {" • "}{auditMeta.externalLinkCount} links externos
                </div>
              )}
              {(() => {
                const timed = routes.filter((r) => typeof r.ms === "number");
                if (timed.length === 0) return null;
                const avg = Math.round(timed.reduce((s, r) => s + (r.ms || 0), 0) / timed.length);
                const max = timed.reduce((m, r) => Math.max(m, r.ms || 0), 0);
                const slowest = timed.reduce((a, b) => ((a.ms || 0) > (b.ms || 0) ? a : b));
                const speedTone = avg < 800 ? "text-emerald-500" : avg < 2000 ? "text-amber-500" : "text-destructive";
                return (
                  <div className="mt-1 text-[10px] sm:text-xs break-words">
                    Velocidade: <span className={`font-semibold ${speedTone}`}>{avg} ms</span> em média
                    {" • "}mais lenta: <span className="font-mono">{slowest.path}</span> ({max} ms)
                    {slowCount > 0 && <> • <span className="text-amber-500">{slowCount} acima de 2s</span></>}
                  </div>
                );
              })()}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <SummaryCard label="Rotas OK" value={okCount} tone="ok" />
          <SummaryCard label="ALERTAS" value={warnCount + externalWarnCount + integrationWarnCount} tone="warn" />
          <SummaryCard label="FALHAS" value={downCount + externalDownCount + headerFailCount} tone="down" />
        </div>


        <Card className="mb-6 sm:mb-8 p-0 overflow-hidden">

          <div className="px-4 sm:px-5 py-4 border-b flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h2 className="font-semibold">PÁGINAS</h2>
              <p className="text-xs text-muted-foreground">Checagem GET manual — evita gerar acessos falsos nas LPs. Clique em Verificar para rodar.</p>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider shrink-0">
              Manual · {ROUTES.length} rotas
            </Badge>
          </div>
          <div className="divide-y">
            {Array.from(new Set(SITE_ROUTES.map((s) => s.groupLabel))).map((groupLabel) => {
              const groupPaths = SITE_ROUTES.filter((s) => s.groupLabel === groupLabel).map((s) => s.path);
              const groupRoutes = routes.filter((r) => groupPaths.includes(r.path));
              if (groupRoutes.length === 0) return null;
              return (
                <div key={groupLabel}>
                  <div className="px-4 sm:px-5 py-2 bg-muted/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {groupLabel} <span className="text-muted-foreground/60">· {groupRoutes.length}</span>
                  </div>
                  {groupRoutes.map((r) => {
                    const meta = SITE_ROUTES.find((s) => s.path === r.path);
                    const href = r.url ?? `https://fisioemortopedia.com.br${r.path}`;
                    const label = href.replace(/^https?:\/\//, "").replace(/\/$/, "");
                    const indexable = meta?.indexable !== false;
                    return (
                      <div
                        key={r.path}
                        className={`grid grid-cols-[minmax(0,1fr)] ${hasChecked ? "sm:grid-cols-[minmax(160px,1.2fr)_minmax(0,2.4fr)_110px_80px_70px]" : "sm:grid-cols-[minmax(160px,1.2fr)_minmax(0,1fr)_110px]"} items-center gap-x-4 gap-y-2 px-4 sm:px-5 py-3 border-t border-border/40 first:border-t-0`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {meta?.label ? (
                            <span className="shrink-0 rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground font-mono truncate max-w-full">
                              {meta.label}
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground truncate">{r.path}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                          {hasChecked && <StatusIcon status={r.status} />}
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs sm:text-sm truncate font-mono text-primary hover:underline min-w-0"
                          >
                            {label}
                          </a>
                        </div>
                        <div className="sm:justify-self-start">
                          <Badge
                            variant="outline"
                            className={`text-[9px] sm:text-[10px] uppercase tracking-wider ${
                              indexable
                                ? "border-emerald-500/40 text-emerald-500"
                                : "border-slate-500/40 text-white/45"
                            }`}
                          >
                            {indexable ? "pública" : "não indexada"}
                          </Badge>
                        </div>
                        {hasChecked && (
                          <>
                            <div className="text-xs font-mono sm:text-right">
                              {r.httpCode ? `HTTP ${r.httpCode}` : ""}
                            </div>
                            <div className="text-xs text-muted-foreground sm:text-right">
                              {r.ms !== undefined ? `${r.ms} ms` : ""}
                            </div>
                          </>
                        )}
                      </div>

                    );

                  })}
                </div>
              );
            })}
          </div>
        </Card>

        <ManagedLinksCard />



        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-4 border-b">
              <h2 className="font-semibold">Cabeçalho institucional</h2>
              <p className="text-xs text-muted-foreground">E-Fisio e LOGIN verificados no HTML real das páginas institucionais.</p>
            </div>
            <div className="divide-y">
              {headerLinks.length === 0 && <div className="px-5 py-4 text-sm text-muted-foreground">Aguardando auditoria…</div>}
              {headerLinks.map((h) => (
                <div key={`${h.path}-${h.label}`} className="px-5 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {h.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-destructive" />}
                      {h.path} · {h.label}
                    </div>
                    <p className="text-xs text-muted-foreground break-all mt-1">Esperado: {h.expectedUrl}</p>
                    {!h.ok && h.foundUrls.length > 0 && <p className="text-xs text-destructive break-all mt-1">Encontrado: {h.foundUrls.join(", ")}</p>}
                  </div>
                  <Badge variant={h.ok ? "default" : "destructive"}>{h.ok ? "ok" : h.found ? "incorreto" : "ausente"}</Badge>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-4 border-b">
              <h2 className="font-semibold">Integrações do backend</h2>
              <p className="text-xs text-muted-foreground">Somente presença real de secrets é marcada como configurada.</p>
            </div>
            <div className="divide-y">
              {integrations.length === 0 && <div className="px-5 py-4 text-sm text-muted-foreground">Aguardando auditoria…</div>}
              {integrations.map((i) => (
                <div key={i.key} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {i.status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
                      {i.label}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{i.detail}</p>
                  </div>
                  <Badge variant={i.status === "ok" ? "default" : "outline"}>{i.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card className="mb-8 p-0 overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold">Links externos</h2>
              <p className="text-xs text-muted-foreground">Extraídos do HTML renderizado e testados por HEAD/GET real.</p>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {externalLinks.length} link(s)
            </Badge>
          </div>
          <div className="divide-y max-h-[520px] overflow-y-auto">
            {externalLinks.length === 0 && <div className="px-5 py-4 text-sm text-muted-foreground">Aguardando auditoria…</div>}
            {externalLinks.map((l) => (
              <div key={l.url} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <StatusIcon status={l.status} />
                  <div className="min-w-0">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm truncate font-mono text-primary hover:underline block">
                      {l.url}
                    </a>
                    <p className="text-[11px] text-muted-foreground truncate">Origem: {l.sourcePaths.join(", ")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs shrink-0">
                  <span>{l.method}</span>
                  {l.httpCode && <span className="font-mono">HTTP {l.httpCode}</span>}
                  <span className="text-muted-foreground">{l.ms} ms</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="p-5">
            <h2 className="font-semibold mb-1">Tags & Pixels</h2>
            <p className="text-xs text-muted-foreground mb-4">Detecção em tempo real no navegador. Todo o tracking é orquestrado pelo Google Tag Manager (Meta Pixel, CAPI e GA4 disparam via GTM).</p>
            <ul className="space-y-3 text-sm">
              <CodeTagRow
                label="Google Tag Manager"
                active={tags.gtm}
                code={codes.gtm}
                fallback="—"
                hint="Não detectado — não instalado"
              />
              <CodeTagRow
                label="Meta Pixel (via GTM)"
                active={tags.metaPixel}
                code={codes.pixel}
                fallback="via GTM"
                hint="GTM ainda não disparou o Pixel nesta página"
              />
              <CodeTagRow
                label="Meta CAPI (via GTM)"
                active={tags.metaPixel}
                code={codes.capi}
                fallback="via GTM"
                hint="CAPI compartilha o Pixel ID · token fica no GTM"
              />
              <CodeTagRow
                label="Google Analytics (via GTM)"
                active={tags.ga4}
                code={codes.ga4}
                fallback="via GTM"
                hint="GTM ainda não disparou o GA4 nesta página"
              />



            </ul>

            {/* dataLayer breakdown */}
            <div className="mt-5 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Eventos no dataLayer</p>
                <Badge variant="outline" className="text-[10px] font-mono">{tags.dataLayerEvents} total</Badge>
              </div>
              {tags.dataLayerBreakdown.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum evento capturado ainda.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {tags.dataLayerBreakdown.map((e) => (
                    <Badge key={e.name} variant="secondary" className="text-[10px] font-mono">
                      {e.name} <span className="ml-1 opacity-60">×{e.count}</span>
                    </Badge>
                  ))}
                </div>
              )}
              {tags.dataLayerRecent.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Ver últimos {tags.dataLayerRecent.length} eventos (payload)
                  </summary>
                  <ul className="mt-2 space-y-1 max-h-60 overflow-y-auto font-mono">
                    {tags.dataLayerRecent.map((e, i) => (
                      <li key={i} className="border-l-2 border-muted pl-2 py-0.5">
                        <div className="text-[10px] font-semibold text-primary">{e.name}</div>
                        <div className="text-[10px] text-muted-foreground break-all">{e.preview}</div>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>

            {/* Consent state */}
            {tags.consent && (
              <div className="mt-5 border-t pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Consent Mode</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(tags.consent).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-[10px] font-mono">
                      {k}: <span className={v === "granted" ? "text-emerald-500 ml-1" : "text-amber-500 ml-1"}>{v}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Tracking cookies */}
            <div className="mt-5 border-t pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Cookies de tracking</p>
              <ul className="grid grid-cols-2 gap-1.5 text-[11px]">
                {tags.trackingCookies.map((c) => (
                  <li key={c.name} className="flex items-center gap-1.5">
                    {c.present ? (
                      <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                    ) : (
                      <XCircle className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                    )}
                    <span className="font-mono">{c.name}</span>
                    <span className="text-muted-foreground truncate">· {c.hint}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-semibold mb-1">Scripts carregados</h2>
            <p className="text-xs text-muted-foreground mb-4">{tags.scripts.length} script(s) externos detectados na página atual.</p>
            <ul className="space-y-2 max-h-72 overflow-y-auto text-xs font-mono">
              {tags.scripts.length === 0 && <li className="text-muted-foreground">Nenhum.</li>}
              {tags.scripts.map((s, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                  <span className="truncate">{s.src}</span>
                  {s.async && <Badge variant="outline" className="text-[10px]">async</Badge>}
                  {s.defer && <Badge variant="outline" className="text-[10px]">defer</Badge>}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Diagnóstico técnico do navegador */}
        {diag && (
          <Card className="p-5 mb-8">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <h2 className="font-semibold">Diagnóstico do navegador & performance</h2>
                <p className="text-xs text-muted-foreground">Métricas capturadas nesta sessão — úteis para reproduzir bugs relatados pela equipe.</p>
              </div>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                {diag.mode === "production" ? "produção" : diag.mode}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <DiagStat label="TTFB" value={`${diag.ttfb} ms`} tone={diag.ttfb < 600 ? "ok" : diag.ttfb < 1500 ? "warn" : "down"} />
              <DiagStat label="DOMContentLoaded" value={`${diag.domContentLoaded} ms`} tone={diag.domContentLoaded < 1500 ? "ok" : diag.domContentLoaded < 3000 ? "warn" : "down"} />
              <DiagStat label="Load event" value={`${diag.loadEvent} ms`} tone={diag.loadEvent < 2500 ? "ok" : diag.loadEvent < 5000 ? "warn" : "down"} />
              <DiagStat label="Transfer" value={`${Math.round(diag.transferSize / 1024)} KB`} tone="ok" />
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <DiagRow label="URL" value={diag.url} mono />
              <DiagRow label="Referrer" value={diag.referrer} mono />
              <DiagRow label="Viewport" value={`${diag.viewport} @ ${diag.dpr}x`} />
              <DiagRow label="Conexão" value={diag.connection} />
              <DiagRow label="User Agent" value={diag.userAgent} mono truncate />
              <DiagRow label="Idioma / Fuso" value={`${diag.lang} · ${diag.tz}`} />
              <DiagRow label="Online" value={diag.online ? "sim" : "OFFLINE"} tone={diag.online ? "ok" : "down"} />
              <DiagRow label="Do Not Track" value={diag.doNotTrack} />
              <DiagRow label="Service Workers" value={String(diag.serviceWorkers)} />
              <DiagRow label="Cookies" value={`${diag.cookiesCount} no domínio atual`} />
              <DiagRow label="localStorage" value={`${diag.localStorageKeys} chave(s)`} />
              <DiagRow label="sessionStorage" value={`${diag.sessionStorageKeys} chave(s)`} />
            </dl>

            <div className="mt-5 pt-4 border-t flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const snapshot = { diag, codes, tags: { ...tags, scripts: tags.scripts.length } };
                  navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
                  toast.success("Snapshot copiado (JSON)");
                }}
              >
                Copiar snapshot técnico (JSON)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(`https://tagassistant.google.com/#/?utm_source=tagmanager&id=${codes.gtm}&url=${encodeURIComponent(window.location.origin)}`, "_blank");
                }}
              >
                Abrir Tag Assistant (GTM)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open("https://www.facebook.com/business/help/198406697184603", "_blank");
                }}
              >
                Meta Events Manager
              </Button>
            </div>
          </Card>
        )}

        {/* Web Vitals — mede APENAS este painel; nunca as LPs */}
        <Card className="p-5 mb-8">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="font-semibold flex items-center gap-2"><Activity className="h-4 w-4" /> Web Vitals (deste painel)</h2>
              <p className="text-xs text-muted-foreground">Coleta local e passiva. Não roda nas LPs — apenas na página atual do admin, para calibrar a experiência da equipe.</p>
            </div>
          </div>
          {Object.keys(vitals).length === 0 ? (
            <p className="text-xs text-muted-foreground">Coletando… (LCP e INP chegam após interação ou algum tempo em tela)</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {["LCP", "INP", "CLS", "FCP", "TTFB"].map((k) => {
                const v = vitals[k];
                if (!v) return <DiagStat key={k} label={k} value="—" tone="ok" />;
                const tone = v.rating === "good" ? "ok" : v.rating === "needs-improvement" ? "warn" : "down";
                const unit = k === "CLS" ? "" : " ms";
                return <DiagStat key={k} label={k} value={`${v.value}${unit}`} tone={tone} />;
              })}
            </div>
          )}
        </Card>

        {/* Sessão Supabase + Build info */}
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="p-5">
            <h2 className="font-semibold mb-1">Sessão Supabase</h2>
            <p className="text-xs text-muted-foreground mb-4">Usuário autenticado neste painel — leitura local, sem chamada de rede adicional.</p>
            {!sessionInfo ? (
              <p className="text-xs text-muted-foreground">Carregando…</p>
            ) : (
              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs">
                <DiagRow label="Email" value={sessionInfo.email} mono />
                <DiagRow label="User ID" value={sessionInfo.userId} mono truncate />
                <DiagRow label="Provider" value={sessionInfo.provider} />
                <DiagRow
                  label="Role admin"
                  value={sessionInfo.isAdmin === "verificando" ? "verificando…" : sessionInfo.isAdmin ? "sim" : "NÃO"}
                  tone={sessionInfo.isAdmin === true ? "ok" : sessionInfo.isAdmin === false ? "down" : undefined}
                />
                <DiagRow label="Expira em" value={sessionInfo.expiresAt} />
                <DiagRow
                  label="Tempo restante"
                  value={`${sessionInfo.expiresInMin} min`}
                  tone={sessionInfo.expiresInMin < 10 ? "down" : undefined}
                />
              </dl>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="font-semibold mb-1">Build & ambiente</h2>
            <p className="text-xs text-muted-foreground mb-4">Versão que a equipe está enxergando agora. Útil para reproduzir bugs "mas aqui funciona".</p>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs">
              <DiagRow label="Modo" value={buildInfo.mode} mono />
              <DiagRow label="Commit" value={buildInfo.commitSha} mono truncate />
              <DiagRow label="Build time" value={buildInfo.buildTime} mono truncate />
              <DiagRow label="Base URL" value={buildInfo.baseUrl} mono />
              <DiagRow label="Hostname" value={typeof window !== "undefined" ? window.location.hostname : ""} mono />
            </dl>
          </Card>
        </div>

        {/* Health check manual */}
        <Card className="p-5 mb-8">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="font-semibold">Health check de integrações</h2>
              <p className="text-xs text-muted-foreground">Verifica <strong>fila do CRM</strong> e <strong>disponibilidade das APIs externas</strong> (RD, Eduzz, Meta). Manual — não gera acessos nas LPs.</p>
            </div>
            <Button size="sm" onClick={doHealthCheck} disabled={healthLoading}>
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${healthLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">{healthLoading ? "Verificando…" : health ? "Re-verificar" : "Executar"}</span>
            </Button>
          </div>
          {!health ? (
            <p className="text-xs text-muted-foreground">Clique em Executar para rodar as verificações.</p>
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground mb-3">Última execução: {new Date(health.generatedAt).toLocaleString("pt-BR")}</p>
              <div className="divide-y">
                {health.results.map((r) => (
                  <div key={r.id} className="py-3 flex items-start gap-3">
                    <StatusIcon status={r.status} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">{r.group}</span>
                        <span className="font-medium text-sm">{r.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">{r.detail}</p>
                      {r.meta && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {Object.entries(r.meta).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px] font-mono">
                              {k}: {String(v)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Erros do navegador */}
        <Card className="p-5 mb-8">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="font-semibold flex items-center gap-2"><Bug className="h-4 w-4" /> Erros do navegador nesta sessão</h2>
              <p className="text-xs text-muted-foreground">Captura passiva de <code>window.onerror</code> e <code>unhandledrejection</code> — apenas enquanto o painel está aberto.</p>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {consoleErrors.length} evento(s)
            </Badge>
          </div>
          {consoleErrors.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum erro capturado.</p>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto text-xs">
              {consoleErrors.map((e, i) => (
                <li key={i} className="border-l-2 border-destructive pl-2 py-1">
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{e.ts}</span>
                    <Badge variant="outline" className="text-[9px] uppercase">{e.type}</Badge>
                  </div>
                  <div className="font-mono text-destructive break-all">{e.message}</div>
                  {e.source && <div className="font-mono text-[10px] text-muted-foreground break-all">{e.source}</div>}
                </li>
              ))}
            </ul>
          )}
        </Card>

      </div>

    </div>
  );
}

function StatusIcon({ status }: { status: RouteStatus["status"] }) {
  if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />;
  if (status === "warn") return <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />;
  if (status === "down") return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
  return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground animate-spin shrink-0" />;
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: "ok" | "warn" | "down" }) {
  const color =
    tone === "ok" ? "text-emerald-500" : tone === "warn" ? "text-amber-500" : "text-destructive";
  return (
    <Card className="p-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
    </Card>
  );
}

function TagRow({ label, active, hint }: { label: string; active: boolean; hint?: string }) {
  return (
    <li className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {active ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
        <span>{label}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        {active ? <Badge variant="default">ativo</Badge> : hint ? <span>{hint}</span> : <Badge variant="outline">inativo</Badge>}
      </div>
    </li>
  );
}

function CodeTagRow({
  label,
  active,
  code,
  fallback,
  hint,
}: {
  label: string;
  active: boolean;
  code: string | null;
  fallback: string;
  hint: string;
}) {
  const display = code ?? fallback;
  return (
    <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {active ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <Badge variant="outline" className="font-mono text-xs">
          {display}
        </Badge>
        {active ? (
          <Badge variant="default">ativo</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{hint}</span>
        )}
      </div>
    </li>
  );
}

function DiagStat({ label, value, tone }: { label: string; value: string; tone: "ok" | "warn" | "down" }) {
  const color = tone === "ok" ? "text-emerald-500" : tone === "warn" ? "text-amber-500" : "text-destructive";
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold font-mono ${color}`}>{value}</p>
    </div>
  );
}

function DiagRow({ label, value, mono, truncate, tone }: { label: string; value: string; mono?: boolean; truncate?: boolean; tone?: "ok" | "down" }) {
  const color = tone === "ok" ? "text-emerald-500" : tone === "down" ? "text-destructive" : "";
  return (
    <div className="flex justify-between gap-3 border-b border-border/40 py-1.5">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className={`${mono ? "font-mono" : ""} ${truncate ? "truncate max-w-[65%]" : "text-right"} ${color}`} title={value}>{value}</dd>
    </div>
  );
}


